import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Sparkles, 
  Shield, 
  BarChart3, 
  Calendar, 
  Building2,
  ArrowRight,
  MessageSquare,
  Target,
  Lock,
  Users,
  ClipboardCheck,
  Zap,
  Moon,
  BookOpen,
  Activity
} from "lucide-react";

export default function Login() {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Capture an `?invite=XXXX` query param so we can auto-redeem after the
  // user completes Google OAuth and lands back on /beta/access.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const inv = params.get("invite");
      if (inv) {
        localStorage.setItem("niko_pending_invite", inv.toUpperCase());
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const featureCategories = [
    {
      title: "Daily Operating Loop",
      features: [
        { icon: Sparkles, text: "Niko's Morning Brief — Reality · Leverage · One Bet", color: "text-violet-400" },
        { icon: Target, text: "Today in 10 Seconds glance bar", color: "text-violet-400" },
        { icon: Zap, text: "Tomorrow Look-Ahead (proactive burnout guard)", color: "text-violet-400" },
        { icon: Moon, text: "Conversational End-of-Day Checkout", color: "text-violet-400" },
      ]
    },
    {
      title: "Leadership & Execution",
      features: [
        { icon: BookOpen, text: "Context-matched Daily Leadership Wisdom", color: "text-emerald-400" },
        { icon: Activity, text: "7-day Energy & Output Trend", color: "text-emerald-400" },
        { icon: Calendar, text: "Unified Calendar · Focus Blocks · Sprints", color: "text-emerald-400" },
        { icon: MessageSquare, text: "Executive Communication Studio", color: "text-emerald-400" },
      ]
    },
    {
      title: "Clinical Ops (when you need it)",
      features: [
        { icon: BarChart3, text: "AI Incident Trend Analysis", color: "text-blue-400" },
        { icon: ClipboardCheck, text: "5 Whys · Fishbone · PDSA · A3 · SBAR", color: "text-blue-400" },
        { icon: Building2, text: "Multi-Facility + AOC tracking", color: "text-blue-400" },
        { icon: Shield, text: "Joint Commission readiness mode", color: "text-blue-400" },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Clock Display - Top Right */}
      <div className="absolute top-6 right-8 z-20 text-right">
        <div className="text-3xl font-mono font-bold text-white tracking-wider">
          {formatTime(currentTime)}
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          {formatDate(currentTime)}
        </div>
      </div>

      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-40 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 -right-40 w-80 h-80 bg-fuchsia-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        {/* Left side - Hero content */}
        <div className="lg:w-2/5 flex flex-col justify-center px-8 lg:px-12 py-12 lg:border-r lg:border-zinc-800">
          <div className="max-w-md">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <span className="text-xl font-bold text-white">N</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                  Niko OS
                </h1>
                <p className="text-xs text-zinc-500">Director's Command Center</p>
              </div>
            </div>
            
            {/* Hero text */}
            <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              The Director's
              <span className="block bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
                Operating System
              </span>
            </h2>
            
            <p className="text-base text-zinc-400 mb-8 leading-relaxed">
              A chief-of-staff in your pocket. Niko synthesizes yesterday, today, and tomorrow into one morning brief — then executes the day with you. Leadership-first. Clinical-fluent. Built for ADHD brains running real hospitals.
            </p>
            
            {/* Security Notice */}
            <div className="p-4 rounded-xl bg-zinc-900/80 border border-zinc-800 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Authorized Access Only</h3>
                  <p className="text-xs text-zinc-400">
                    Access is restricted to verified healthcare professionals. 
                    Your organization must be registered to use Niko OS.
                  </p>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <Button
              data-testid="login-with-google-btn"
              onClick={handleLogin}
              className="w-full h-14 text-base font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 shadow-lg shadow-violet-500/25 transition-all hover:shadow-violet-500/40"
              size="lg"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign In with Organizational Account
              <ArrowRight className="w-5 h-5 ml-3" />
            </Button>
            
            <div className="flex items-center gap-4 mt-4 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                HIPAA Compliant
              </div>
              <div className="flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" />
                SOC 2 Ready
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                SSO Enabled
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - All Features */}
        <div className="lg:w-3/5 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              One brain. Two modes. Protected daily.
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featureCategories.map((category, idx) => (
                <div key={idx} className="space-y-3">
                  <h4 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">
                    {category.title}
                  </h4>
                  <div className="space-y-2">
                    {category.features.map((feature, fidx) => (
                      <div 
                        key={fidx}
                        className="flex items-center gap-2 p-2 rounded-lg bg-zinc-900/30 border border-zinc-800/50 hover:border-zinc-700 hover:bg-zinc-900/50 transition-colors"
                      >
                        <feature.icon className={`w-4 h-4 ${feature.color} flex-shrink-0`} />
                        <span className="text-sm text-zinc-300">{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Additional highlights */}
            <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-violet-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm mb-1">Built for directors who run real hospitals</h4>
                  <p className="text-xs text-zinc-400">
                    Designed for Quality Directors, Infection Preventionists, and CNOs juggling patient safety, staffing, and regulatory pressure — plus a real life. HIPAA, SOC 2, and Joint Commission aligned.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-xs text-zinc-600">
          © 2026 Niko OS • Enterprise Healthcare Platform • Contact: support@nikosafe.com
        </p>
      </div>
    </div>
  );
}
